import React, { Component } from 'react';
import { array, arrayOf, bool, func, number, object, string } from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import {
  txIsCanceled,
  txIsEnquired,
  txIsRequestedContact,
  txIsInProgress,
  txIsDelivered,
  txIsWaitingForDocuments,
  txIsDocumentUploaded,

  isLastTransition,

  TRANSITION_REQUEST,
  TRANSITION_SEND_OFFER,
  TRANSITION_SEND_CALENDAR_LINK,
  TRANSITION_SEND_COUNTEROFFER_BY_CUSTOMER,
  TRANSITION_SEND_COUNTEROFFER_BY_PROVIDER,
  TRANSITION_APPROVE_OFFER,
  TRANSITION_APPROVE_COUNTEROFFER,
  TRANSITION_UPLOAD_DOCUMENTS,
  TRANSITION_UPLOAD_SIGNED_DOCUMENTS,
  TRANSITION_REQUEST_OFFER_COMPLETE,
  TRANSITION_ACCEPT_OFFER_COMPLETE,
  TRANSITION_DECLINE_OFFER_COMPLETE,
  TRANSITION_CANCELL_OFFER_BY_CUSTOMER,
  TRANSITION_CANCELL_OFFER_BY_PROVIDER,

  TRANSITION_OPERATOR_CANCEL_ENQUIRY,
  TRANSITION_OPERATOR_CANCEL_REQUESTED,
  TRANSITION_OPERATOR_CANCEL_COUNTEROFFER_RECEIVED,
  TRANSITION_OPERATOR_CANCEL_OFFER_RECEIVED,

  TRANSITION_OPERATOR_FINISH_WAITING_FOR_DOCUMENTS,
  TRANSITION_OPERATOR_FINISH_DOCUMENTS_UPLOADED,
  TRANSITION_OPERATOR_FINISH_IN_PROGRESS,
  TRANSITION_OPERATOR_FINISH_WORK_COMPLETED,
} from '../../util/transaction';
import { LINE_ITEM_NIGHT, LINE_ITEM_DAY, propTypes } from '../../util/types';
import {
  ensureListing,
  ensureTransaction,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { isMobileSafari } from '../../util/userAgent';
import { formatMoney } from '../../util/currency';
import {
  AvatarLarge,
  BookingPanel,
  NamedLink,
  ReviewModal,
  UserDisplayName,
  Button,
  PrimaryButton,
  SecondaryButton,
  Modal,
  OfferDetails,
  EndNegotiation
} from '../../components';
import { SendMessageForm, EnquiryForm } from '../../forms';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
// These are internal components that make this file more readable.
import AddressLinkMaybe from './AddressLinkMaybe';
import BreakdownMaybe from './BreakdownMaybe';
import DetailCardHeadingsMaybe from './DetailCardHeadingsMaybe';
import DetailCardImage from './DetailCardImage';
import FeedSection from './FeedSection';
import SaleActionButtonsMaybe from './SaleActionButtonsMaybe';

import PanelHeading, {
  HEADING_ENQUIRED,
  HEADING_ENQUIREDRFQ,
  HEADING_REQUESTED,
  HEADING_OFFER_RECEIVED,
  HEADING_COUNTEROFFER_RECEIVED,
  HEADING_COUNTEROFFER_RECEIVED_FROM_PROVIDER,
  HEADING_CANCELED,
  HEADING_CANCELED_BY_PROVIDER,
  HEADING_OFFER_APPROVED,
  HEADING_OFFER_APPROVED_BY_PROVIDER,
  HEADING_UPLOAD_DOCUMENTS,
  HEADING_IN_PROGRESS,
  HEADING_DECLINED,
  HEADING_COMPLETE_OFFER,
  HEADING_DELIVERED,
  HEADING_CANCELED_BY_OPERATOR,
  HEADING_DELIVERED_BY_OPERATOR,
} from './PanelHeading';

import { FileUploadForm } from '../../forms';

import css from './TransactionPanel.module.css';
import ContactLinksForm from './ContactLinksForm/ContactLinksForm';
import { fixUrl } from '../../util/urlHelpers';

const PROVIDER_DOCUMENTS = "providerDocuments";
const CUSTOMER_DOCUMENTS = "customerDocuments";

const OFFERS_LIMIT = 5;
const COMPLETE_REQUESTS_LIMIT = 5;

const OFFER = "offer";
const REQUEST = "request";

// Helper function to get display names for different roles
const displayNames = (currentUser, currentProvider, currentCustomer, intl) => {
  const authorDisplayName = <UserDisplayName user={currentProvider} intl={intl} />;
  const customerDisplayName = <UserDisplayName user={currentCustomer} intl={intl} />;

  let otherUserDisplayName = '';
  let otherUserDisplayNameString = '';
  const currentUserIsCustomer =
    currentUser.id && currentCustomer.id && currentUser.id.uuid === currentCustomer.id.uuid;
  const currentUserIsProvider =
    currentUser.id && currentProvider.id && currentUser.id.uuid === currentProvider.id.uuid;

  if (currentUserIsCustomer) {
    otherUserDisplayName = authorDisplayName;
    otherUserDisplayNameString = currentProvider?.attributes?.profile?.publicData?.companyName || userDisplayNameAsString(currentProvider, '');
  } else if (currentUserIsProvider) {
    otherUserDisplayName = customerDisplayName;
    otherUserDisplayNameString = currentCustomer?.attributes?.profile?.publicData?.companyName || userDisplayNameAsString(currentCustomer, '');
  }

  return {
    authorDisplayName,
    customerDisplayName,
    otherUserDisplayName,
    otherUserDisplayNameString,
  };
};

export class TransactionPanelComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sendMessageFormFocused: false,
      isReviewModalOpen: false,
      reviewSubmitted: false,
      enquiryModalOpen: false,
      offerDetailsOpen: false,
      cancellOfferOpen: false,
      declineOfferCompleteOpen: false,

      uploadFileOpen: false,
      uploadFileType: false,

      overLimitOpen: false,
      overLimitType: null,
    };
    this.isMobSaf = false;
    this.sendMessageFormName = 'TransactionPanel.SendMessageForm';

    this.onOpenReviewModal = this.onOpenReviewModal.bind(this);
    this.onOpenOfferModal = this.onOpenOfferModal.bind(this);
    this.onOpenCancellOffer = this.onOpenCancellOffer.bind(this);

    this.onOpenDeclineOfferComplete = this.onOpenDeclineOfferComplete.bind(this);
    this.onDeclineOffer = this.onDeclineOffer.bind(this);

    this.onOpenUploadFile = this.onOpenUploadFile.bind(this);
    this.onCloseUploadFile = this.onCloseUploadFile.bind(this);

    this.onOpenOverLimit = this.onOpenOverLimit.bind(this);
    this.onCloseOverLimit = this.onCloseOverLimit.bind(this);

    this.onSendMessageFormFocus = this.onSendMessageFormFocus.bind(this);
    this.onSendMessageFormBlur = this.onSendMessageFormBlur.bind(this);
    this.onMessageSubmit = this.onMessageSubmit.bind(this);
    this.scrollToMessage = this.scrollToMessage.bind(this);
    this.onSubmitEnquiry = this.onSubmitEnquiry.bind(this);

    this.onSubmitOfferDetails = this.onSubmitOfferDetails.bind(this);
    this.onSubmitUploadedFile = this.onSubmitUploadedFile.bind(this);

    this.onSubmitCancellOffer = this.onSubmitCancellOffer.bind(this);

    this.onApproveOfferOrCuonterOffer = this.onApproveOfferOrCuonterOffer.bind(this);
  }

  componentDidMount() {
    this.isMobSaf = isMobileSafari();

    const { currentUser, transaction, onUpdateTransaction, onFetchCurrentUser } = this.props;

    const currentTransaction = ensureTransaction(transaction);
    const transactionId = currentTransaction.id.uuid;


    if (currentUser) {
      const unreadMessages = transaction?.attributes?.metadata?.unreadMessages || {};
      const viewed = transaction?.attributes?.metadata?.viewed || {};

      if (!!unreadMessages[currentUser.id.uuid]) {
        const updatedData = {
          ...unreadMessages,
          [currentUser.id.uuid]: 0,
        };
        onUpdateTransaction({
          id: transactionId,
          metadata: {
            unreadMessages: updatedData,
            viewed: {
              ...viewed,
              [currentUser.id.uuid]: true,
            }
          },
        }).then(() => {
          onFetchCurrentUser();
        }).catch(error => {
          console.error(error);
        });;

      } else if (!viewed[currentUser.id.uuid]) {
        onUpdateTransaction({
          id: transactionId,
          metadata: {
            viewed: {
              ...viewed,
              [currentUser.id.uuid]: true,
            }
          },
        }).then(() => {
          onFetchCurrentUser();
        }).catch(error => {
          console.error(error);
        });;
      }
    }
  }

  onOpenReviewModal() {
    this.setState({ isReviewModalOpen: true });
  }

  onOpenOfferModal() {
    this.setState({ offerDetailsOpen: true });
  }

  onOpenCancellOffer() {
    this.setState({ cancellOfferOpen: true });
  }

  onOpenUploadFile(type) {
    this.setState({ uploadFileType: type });
    this.setState({ uploadFileOpen: true });
  }

  onCloseUploadFile() {
    this.setState({ uploadFileType: null });
    this.setState({ uploadFileOpen: false });
  }

  onOpenOverLimit(type) {
    this.setState({ overLimitType: type });
    this.setState({ overLimitOpen: true });
  }

  onCloseOverLimit() {
    this.setState({ overLimitType: null });
    this.setState({ overLimitOpen: false });
  }

  onSendMessageFormFocus() {
    this.setState({ sendMessageFormFocused: true });
    if (this.isMobSaf) {
      // Scroll to bottom
      window.scroll({ top: document.body.scrollHeight, left: 0, behavior: 'smooth' });
    }
  }

  onSendMessageFormBlur() {
    this.setState({ sendMessageFormFocused: false });
  }

  onMessageSubmit(values, form) {
    const message = values.message ? values.message?.trim() : null;
    const {
      provider,
      customer,
      currentUser,
      transaction,
      onSendMessage,
      transactionRole,
      onSetUnreadMessagesToTx,
    } = this.props;

    if (!message) {
      return;
    }

    const currentTransaction = ensureTransaction(transaction);
    const transactionId = currentTransaction.id.uuid;

    const otherUserId = transactionRole === 'provider' ? customer.id.uuid : provider.id.uuid;

    onSendMessage(transaction, otherUserId, message, config)
      .then(messageId => {
        form.reset();
        this.scrollToMessage(messageId);
      })
      .then(() =>
        onSetUnreadMessagesToTx({ txId: transactionId, userId: otherUserId })
      )
      .catch(e => console.log(e));
  }

  scrollToMessage(messageId) {
    const selector = `#msg-${messageId.uuid}`;
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  }

  onSubmitEnquiry(values) {
    const { history, onSendEnquiry, transaction } = this.props;
    const currentTransaction = ensureTransaction(transaction);
    const routes = routeConfiguration();
    const enquiryValues = { ...values };

    onSendEnquiry(currentTransaction, enquiryValues)
      .then(txId => {
        this.setState({ enquiryModalOpen: false });
        // Redirect to OrderDetailsPage
        history.push(
          createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {})
        );
      })
      .catch((e) => {
        console.log(e);
        // Ignore, error handling in duck file
      });
  }

  onSubmitOfferDetails(values) {
    const { onSendOffer, transaction, transactionRole } = this.props;
    const isCustomer = transactionRole === 'customer';
    const isProvider = transactionRole === 'provider';
    const currentTransaction = ensureTransaction(transaction);

    if (isCustomer) {
      onSendOffer(values, currentTransaction, TRANSITION_SEND_COUNTEROFFER_BY_CUSTOMER);
    } else if (isProvider && transaction?.attributes?.lastTransition === TRANSITION_SEND_COUNTEROFFER_BY_CUSTOMER) {
      onSendOffer(values, currentTransaction, TRANSITION_SEND_COUNTEROFFER_BY_PROVIDER);
    } else {
      onSendOffer(values, currentTransaction, TRANSITION_SEND_OFFER);
    }

    this.setState({ offerDetailsOpen: false });
    this.setState({ overLimitType: null });
    this.setState({ overLimitOpen: false });
  }

  onApproveOfferOrCuonterOffer() {
    const { onApproveOffer, transaction, transactionRole } = this.props;
    const isCustomer = transactionRole === 'customer';
    const isProvider = transactionRole === 'provider';
    const currentTransaction = ensureTransaction(transaction);

    if (isCustomer && transaction?.attributes?.lastTransition === TRANSITION_SEND_COUNTEROFFER_BY_CUSTOMER) {
      onApproveOffer(currentTransaction, TRANSITION_APPROVE_COUNTEROFFER);
    } else if (isProvider && transaction?.attributes?.lastTransition === TRANSITION_SEND_COUNTEROFFER_BY_CUSTOMER) {
      onApproveOffer(currentTransaction, TRANSITION_APPROVE_COUNTEROFFER);
    } else {
      onApproveOffer(currentTransaction, TRANSITION_APPROVE_OFFER);
    }
  }

  onSubmitUploadedFile(values) {
    const { onUploadFile, transaction } = this.props;
    const { uploadFileType } = this.state;
    const currentTransaction = ensureTransaction(transaction);

    if (uploadFileType === PROVIDER_DOCUMENTS) {
      onUploadFile(values, currentTransaction, 'provider');
    } else {
      onUploadFile(values, currentTransaction, 'customer');
    }
    this.setState({ uploadFileOpen: false });
  }

  onSubmitCancellOffer() {
    const { onCancellOffer, transaction, transactionRole } = this.props;
    const isCustomer = transactionRole === 'customer';
    const isProvider = transactionRole === 'provider';
    const currentTransaction = ensureTransaction(transaction);

    if (isProvider) {
      onCancellOffer(currentTransaction, TRANSITION_CANCELL_OFFER_BY_PROVIDER);
    } else {
      onCancellOffer(currentTransaction, TRANSITION_CANCELL_OFFER_BY_CUSTOMER);
    }
    this.setState({ cancellOfferOpen: false });
  }

  onOpenDeclineOfferComplete() {
    this.setState({ declineOfferCompleteOpen: true });
  }

  onDeclineOffer(desclineDescription) {
    const { onCancellOffer, transaction, transactionRole, onCompleteOfferRequest } = this.props;
    const isCustomer = transactionRole === 'customer';
    const isProvider = transactionRole === 'provider';
    const currentTransaction = ensureTransaction(transaction);

    onCompleteOfferRequest(currentTransaction, TRANSITION_DECLINE_OFFER_COMPLETE, desclineDescription)
    this.setState({ declineOfferCompleteOpen: false });
  }


  render() {
    const {
      rootClassName,
      className,
      currentUser,
      transaction,
      totalMessagePages,
      oldestMessagePageFetched,
      messages,
      initialMessageFailed,
      savePaymentMethodFailed,
      fetchMessagesInProgress,
      fetchMessagesError,
      sendMessageInProgress,
      sendMessageError,
      sendReviewInProgress,
      sendReviewError,
      onFetchTimeSlots,
      onManageDisableScrolling,
      onShowMoreMessages,
      transactionRole,
      intl,
      onSubmitBookingRequest,
      monthlyTimeSlots,
      nextTransitions,
      onFetchTransactionLineItems,
      onFetchTransaction,
      lineItems,
      fetchLineItemsInProgress,
      fetchLineItemsError,
      onSendCalendarLink,
      sendEnquiryInProgress,
      sendEnquiryError,
      history,

      onApproveOffer,
      onCompleteOfferRequest,
    } = this.props;

    const currentTransaction = ensureTransaction(transaction);
    const currentListing = ensureListing(currentTransaction.listing);
    const currentProvider = ensureUser(currentTransaction.provider);
    const currentCustomer = ensureUser(currentTransaction.customer);
    const isCustomer = transactionRole === 'customer';
    const isProvider = transactionRole === 'provider';

    const listingLoaded = !!currentListing.id;
    const listingDeleted = listingLoaded && currentListing.attributes.deleted;
    const iscustomerLoaded = !!currentCustomer.id;
    const isCustomerBanned = iscustomerLoaded && currentCustomer.attributes.banned;
    const isCustomerDeleted = iscustomerLoaded && currentCustomer.attributes.deleted;
    const isProviderLoaded = !!currentProvider.id;
    const isProviderBanned = isProviderLoaded && currentProvider.attributes.banned;
    const isProviderDeleted = isProviderLoaded && currentProvider.attributes.deleted;

    const questionsObj = currentTransaction?.attributes?.protectedData?.questions;
    const questionsArray = questionsObj ? Object.entries(questionsObj) : [];
    const hasQuestions = questionsArray?.length > 0;

    const lastTransition = currentTransaction?.attributes?.lastTransition;

    const calendarLink = currentTransaction?.attributes?.metadata?.calendarLink ||
      currentTransaction?.attributes?.protectedData?.calendarLink;

    const invoice = currentTransaction?.attributes?.metadata?.invoice ||
      currentTransaction?.attributes?.protectedData?.invoice;

    const contract = currentTransaction?.attributes?.metadata?.contract ||
      currentTransaction?.attributes?.protectedData?.contract;

    const signedContract = currentTransaction?.attributes?.metadata?.signedContract ||
      currentTransaction?.attributes?.protectedData?.signedContract;

    const transitions = currentTransaction?.attributes?.transitions || [];

    const showUploadInvoice = !!transitions?.filter(
      t => t.transition === TRANSITION_APPROVE_OFFER || t.transition === TRANSITION_APPROVE_COUNTEROFFER
    )?.[0];

    const offersCounter = transaction?.attributes?.protectedData?.offerCounter || 0
    const completeOfferRequestCounter = transaction?.attributes?.protectedData?.completeOfferRequestCounter || 0

    const stateDataFn = tx => {
      if (txIsEnquired(tx)) {
        return {
          headingState: HEADING_ENQUIRED,
          showBookingPanel: true,
          showRFQ: isCustomer
        };
      }
      else if (txIsRequestedContact(tx)) {
        return {
          headingState: HEADING_ENQUIREDRFQ,
          showBookingPanel: true,
          showSendOffer: isProvider,
        };
      }
      else if (isLastTransition(tx, TRANSITION_REQUEST) || isLastTransition(tx, TRANSITION_SEND_CALENDAR_LINK)) {
        return {
          headingState: HEADING_ENQUIREDRFQ,
          // headingState: HEADING_REQUESTED,
          showBookingPanel: true,

          showSendOffer: isProvider
        };
      } else if (isLastTransition(tx, TRANSITION_SEND_OFFER)) {
        return {
          headingState: HEADING_OFFER_RECEIVED,
          showBookingPanel: true,
          showApproveOffer: isCustomer,
          showSendCounterOffer: isCustomer && offersCounter <= OFFERS_LIMIT,
          showCancellOffer: isCustomer,
          showProviderWaitText: isProvider
        };
      }
      else if (isLastTransition(tx, TRANSITION_SEND_COUNTEROFFER_BY_CUSTOMER)) {
        return {
          headingState: HEADING_COUNTEROFFER_RECEIVED,
          showBookingPanel: true,
          showApproveOffer: isProvider,
          showSendCounterOffer: isProvider && offersCounter <= OFFERS_LIMIT + 1,
          showCancellOffer: isProvider,
        };
      }
      else if (isLastTransition(tx, TRANSITION_SEND_COUNTEROFFER_BY_PROVIDER)) {
        return {
          headingState: HEADING_COUNTEROFFER_RECEIVED_FROM_PROVIDER,
          showBookingPanel: true,
          showApproveOffer: isCustomer,
          showSendCounterOffer: isCustomer && offersCounter <= OFFERS_LIMIT,
          showCancellOffer: isCustomer,
        };
      }
      else if (isLastTransition(tx, TRANSITION_CANCELL_OFFER_BY_CUSTOMER)) {
        return {
          headingState: HEADING_CANCELED,
          showBookingPanel: true,
        };
      }
      else if (isLastTransition(tx, TRANSITION_CANCELL_OFFER_BY_PROVIDER)) {
        return {
          headingState: HEADING_CANCELED_BY_PROVIDER,
          showBookingPanel: true,
        };
      }


      // else if (txIsWaitingForDocuments(tx)) {

      //   let heading;

      //   if (isLastTransition(tx, TRANSITION_APPROVE_OFFER)) heading = HEADING_OFFER_APPROVED
      //   else if (isLastTransition(tx, TRANSITION_APPROVE_COUNTEROFFER)) heading = HEADING_OFFER_APPROVED_BY_PROVIDER

      //   return {
      //     headingState: heading,
      //     showBookingPanel: true,
      //     // showPay: isCustomer && tx?.attributes?.lastTransition !== TRANSITION_DECLINE_OFFER_COMPLETE,
      //     showUploadInvoice: isProvider && !invoice,
      //     showUploadContract: isProvider && !contract,
      //     showUploadSignedContract: isCustomer && contract,
      //     // showCompleteOfferRequest: isProvider && invoice && contract,
      //     showProviderWaitForPayText: isProvider
      //   };
      // }
      // else if (txIsDocumentUploaded(tx)) {
      //   return {
      //     headingState: HEADING_UPLOAD_DOCUMENTS,
      //     showBookingPanel: true,
      //     // showPay: isCustomer && tx?.attributes?.lastTransition !== TRANSITION_DECLINE_OFFER_COMPLETE,
      //     showUploadSignedContract: isCustomer && contract,
      //     // showCompleteOfferRequest: isProvider && invoice && contract,
      //     showProviderWaitForPayText: isProvider
      //   };
      // }


      else if (isLastTransition(tx, TRANSITION_DECLINE_OFFER_COMPLETE)) {
        return {
          headingState: HEADING_DECLINED,
          showBookingPanel: true,
          showCompleteOfferRequest: isProvider,
        };
      }
      else if (isLastTransition(tx, TRANSITION_APPROVE_OFFER) ||
        isLastTransition(tx, TRANSITION_APPROVE_COUNTEROFFER)
      ) {
        return {
          headingState: HEADING_IN_PROGRESS,
          showBookingPanel: true,
          showCompleteOfferRequest: isProvider && completeOfferRequestCounter <= COMPLETE_REQUESTS_LIMIT,
        };
      }
      else if (isLastTransition(tx, TRANSITION_REQUEST_OFFER_COMPLETE)) {
        return {
          headingState: HEADING_COMPLETE_OFFER,
          showBookingPanel: true,
          showCompleteOfferApprove: isCustomer,
          showCompleteOfferDecline: isCustomer && completeOfferRequestCounter <= COMPLETE_REQUESTS_LIMIT,
        };
      }
      else if (txIsDelivered(tx)) {
        return {
          headingState: HEADING_DELIVERED,
          showBookingPanel: true,
        };
      }
      else if (
        isLastTransition(tx, TRANSITION_OPERATOR_CANCEL_ENQUIRY) ||
        isLastTransition(tx, TRANSITION_OPERATOR_CANCEL_REQUESTED) ||
        isLastTransition(tx, TRANSITION_OPERATOR_CANCEL_COUNTEROFFER_RECEIVED) ||
        isLastTransition(tx, TRANSITION_OPERATOR_CANCEL_OFFER_RECEIVED)
      ) {
        return {
          headingState: HEADING_CANCELED_BY_OPERATOR,
          showBookingPanel: true,
        };
      }
      else if (
        isLastTransition(tx, TRANSITION_OPERATOR_FINISH_WAITING_FOR_DOCUMENTS,) ||
        isLastTransition(tx, TRANSITION_OPERATOR_FINISH_DOCUMENTS_UPLOADED,) ||
        isLastTransition(tx, TRANSITION_OPERATOR_FINISH_IN_PROGRESS,) ||
        isLastTransition(tx, TRANSITION_OPERATOR_FINISH_WORK_COMPLETED,)
      ) {
        return {
          headingState: HEADING_DELIVERED_BY_OPERATOR,
          showBookingPanel: true,
        };
      }
      else {
        return { headingState: 'unknown' };
      }
    };

    const stateData = stateDataFn(currentTransaction);

    const deletedListingTitle = intl.formatMessage({
      id: 'TransactionPanel.deletedListingTitle',
    });

    const {
      authorDisplayName,
      customerDisplayName,
      otherUserDisplayName,
      otherUserDisplayNameString,
    } = displayNames(currentUser, currentProvider, currentCustomer, intl);

    const { publicData, geolocation } = currentListing.attributes;
    const location = publicData && publicData.location ? publicData.location : {};
    const listingTitle = currentListing.attributes.deleted
      ? deletedListingTitle
      : !!currentListing?.attributes?.publicData?.customDisplayName ?
        currentListing?.attributes?.publicData?.customDisplayName
        : currentListing.attributes.title

    const customDisplayName = currentListing?.attributes?.publicData?.customDisplayName;

    const unitType = config.bookingUnitType;
    const isNightly = unitType === LINE_ITEM_NIGHT;
    const isDaily = unitType === LINE_ITEM_DAY;

    const unitTranslationKey = isNightly
      ? 'TransactionPanel.perNight'
      : isDaily
        ? 'TransactionPanel.perDay'
        : 'TransactionPanel.perUnit';

    const price = currentListing.attributes.price;
    const bookingSubTitle = price
      ? `${formatMoney(intl, price)} ${intl.formatMessage({ id: unitTranslationKey })}`
      : '';

    const firstImage =
      currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

    const showSendMessageForm =
      !isCustomerBanned && !isCustomerDeleted && !isProviderBanned && !isProviderDeleted;
    const sendMessagePlaceholder = intl.formatMessage(
      { id: 'TransactionPanel.sendMessagePlaceholder' },
      { name: isProvider ? otherUserDisplayNameString : listingTitle }
    );

    const sendingMessageNotAllowed = intl.formatMessage({
      id: 'TransactionPanel.sendingMessageNotAllowed',
    });

    const paymentMethodsPageLink = (
      <NamedLink name="PaymentMethodsPage">
        <FormattedMessage id="TransactionPanel.paymentMethodsPageLink" />
      </NamedLink>
    );

    const classes = classNames(rootClassName || css.root, className);
    const customerCompanyName = currentTransaction?.customer?.attributes?.profile?.publicData?.companyName || customerDisplayName
    const providerCompanyName = currentTransaction?.provider?.attributes?.profile?.publicData?.companyName || authorDisplayName

    const {
      enquiryModalOpen,
      offerDetailsOpen,
      uploadFileOpen,
      uploadFileType,
      overLimitOpen,
      overLimitType,
      declineOfferCompleteOpen
    } = this.state;

    return (
      <div className={classes}>
        <div className={css.container}>

          <div className={css.panelHeadingMobile}>
            <PanelHeading
              panelHeadingState={stateData.headingState}
              transactionRole={transactionRole}
              providerName={providerCompanyName}
              customerName={customerCompanyName}
              isCustomerBanned={isCustomerBanned}
              listingId={currentListing.id && currentListing.id.uuid}
              listingTitle={listingTitle}
              listingDeleted={listingDeleted}
            />
          </div>

          <div className={css.txInfo}>
            {/* <DetailCardImage
              rootClassName={css.imageWrapperMobile}
              avatarWrapperClassName={css.avatarWrapperMobile}
              listingTitle={listingTitle}
              image={firstImage}
              provider={currentProvider}
              customer={currentCustomer}
              isCustomer={isCustomer}
              listingId={currentListing.id && currentListing.id.uuid}
              listingDeleted={listingDeleted}
            />
            <DetailCardHeadingsMaybe
              showDetailCardHeadings
              listingTitle={listingTitle}
              subTitle={bookingSubTitle}
              location={location}
              geolocation={geolocation}
              showAddress={stateData.showAddress}
              transaction={currentTransaction}
              isCustomer={isCustomer}
              isMobile
            /> */}

            {isProvider ? (
              <div className={css.avatarWrapperProviderDesktop}>
                <AvatarLarge user={currentCustomer} className={css.avatarDesktop} />
              </div>
            ) : null}


            <div className={css.panelHeadingDesktop}>
              <PanelHeading
                panelHeadingState={stateData.headingState}
                transactionRole={transactionRole}
                providerName={providerCompanyName}
                customerName={customerCompanyName}
                isCustomerBanned={isCustomerBanned}
                listingId={currentListing.id && currentListing.id.uuid}
                listingTitle={listingTitle}
                listingDeleted={listingDeleted}
              />
            </div>

            {/* <div className={css.bookingDetailsMobile}>
              <AddressLinkMaybe
                rootClassName={css.addressMobile}
                location={location}
                geolocation={geolocation}
                showAddress={stateData.showAddress}
              />
              <BreakdownMaybe transaction={currentTransaction} transactionRole={transactionRole} />
            </div> */}

            {savePaymentMethodFailed ? (
              <p className={css.genericError}>
                <FormattedMessage
                  id="TransactionPanel.savePaymentMethodFailed"
                  values={{ paymentMethodsPageLink }}
                />
              </p>
            ) : null}
            <FeedSection
              rootClassName={css.feedContainer}
              currentTransaction={currentTransaction}
              currentUser={currentUser}
              fetchMessagesError={fetchMessagesError}
              fetchMessagesInProgress={fetchMessagesInProgress}
              initialMessageFailed={initialMessageFailed}
              messages={messages}
              oldestMessagePageFetched={oldestMessagePageFetched}
              onOpenReviewModal={this.onOpenReviewModal}
              onShowMoreMessages={() => onShowMoreMessages(currentTransaction.id)}
              totalMessagePages={totalMessagePages}
            />
            {showSendMessageForm ? (
              <SendMessageForm
                formId={this.sendMessageFormName}
                rootClassName={css.sendMessageForm}
                messagePlaceholder={sendMessagePlaceholder}
                inProgress={sendMessageInProgress}
                sendMessageError={sendMessageError}
                onFocus={this.onSendMessageFormFocus}
                onBlur={this.onSendMessageFormBlur}
                onSubmit={this.onMessageSubmit}
              />
            ) : (
              <div className={css.sendingMessageNotAllowed}>{sendingMessageNotAllowed}</div>

            )}
          </div>

          <div className={css.asideDesktop}>
            <div className={css.detailCard}>
              <DetailCardImage
                avatarWrapperClassName={css.avatarWrapperDesktop}
                listingTitle={listingTitle}
                image={firstImage}
                provider={currentProvider}
                customer={currentCustomer}
                isCustomer={isCustomer}
                listingId={currentListing.id && currentListing.id.uuid}
                listingDeleted={listingDeleted}
              />

              {stateData.showBookingPanel && (
                <BookingPanel
                  className={css.bookingPanel}
                  titleClassName={css.bookingTitle}
                  isOwnListing={false}
                  listing={currentListing}
                  title={listingTitle}
                  subTitle={bookingSubTitle}
                  authorDisplayName={authorDisplayName}
                  onSubmit={onSubmitBookingRequest}
                  onManageDisableScrolling={onManageDisableScrolling}
                  monthlyTimeSlots={monthlyTimeSlots}
                  onFetchTimeSlots={onFetchTimeSlots}
                  onFetchTransactionLineItems={onFetchTransactionLineItems}
                  lineItems={lineItems}
                  fetchLineItemsInProgress={fetchLineItemsInProgress}
                  fetchLineItemsError={fetchLineItemsError}
                  currentTransaction={currentTransaction}
                  isProvider={isProvider}
                  currentUser={currentUser}
                />
              )}

              <DetailCardHeadingsMaybe
                showDetailCardHeadings
                listingTitle={listingTitle}
                subTitle={bookingSubTitle}
                location={location}
                geolocation={geolocation}
                showAddress={stateData.showAddress}
                transaction={currentTransaction}
                isCustomer={isCustomer}
              />

              <div className={classNames(css.questionsWrapper, { [css.questionsWrapperBorder]: isCustomer })}>
                {questionsArray.map(q => {
                  if (typeof (q?.[1]) === "string") {
                    return (
                      <div className={css.question}>
                        <p>
                          <strong>{q[0] && q[0].replaceAll('-', ' ').replaceAll('*', '.')}</strong>
                        </p>
                        <p>{q[1]}</p>
                      </div>
                    )
                  } else {
                    return null
                  }
                })}
              </div>
              <BreakdownMaybe
                className={css.breakdownContainer}
                transaction={currentTransaction}
                transactionRole={transactionRole}
              />
              <div className={css.enquiryActionsWrapper}>
                {isProvider && hasQuestions && !calendarLink && (
                  <ContactLinksForm
                    onSendCalendarLink={onSendCalendarLink}
                    onFetchTransaction={onFetchTransaction}
                    currentTransaction={currentTransaction}
                    hideTitle={!!calendarLink}
                  />
                )}
                {calendarLink && (
                  <div className={classNames(css.calendarLink, { [css.calendarLinkCustomer]: isCustomer })}>
                    <a target="_blank" href={fixUrl(calendarLink)}>
                      {/* Calendar meeting */}
                      {isCustomer ? "Book a meeting" : "Your Calendar link"}
                    </a>
                  </div>
                )}
                {/* {stateData.showProviderWaitForPayText && (
                  <p className={css.infoText}>
                    <FormattedMessage id="TransactionPanel.waitForPay" />
                  </p>
                )} */}
                {stateData.showSendOffer && (
                  <Button
                    className={css.actionButton}
                    onClick={() => this.onOpenOfferModal()
                    }
                  >
                    <FormattedMessage id="TransactionPanel.sendAquote" />
                  </Button>
                )}
                {stateData.showRFQ && (
                  <PrimaryButton
                    className={css.actionButton}
                    onClick={() => this.setState({ enquiryModalOpen: true })}
                  >
                    <FormattedMessage id="TransactionPanel.requestForQuote" />
                  </PrimaryButton>
                )}
                {stateData.showApproveOffer && (
                  <Button
                    className={classNames(css.actionButton, css.actionButtonBlue)}
                    onClick={() => this.onApproveOfferOrCuonterOffer()}
                  >
                    <FormattedMessage id="TransactionPanel.acceptTheQuote" />
                  </Button>
                )}
                {stateData.showSendCounterOffer && (
                  <Button
                    className={classNames(css.actionButton, css.actionButtonWhite)}
                    onClick={() => offersCounter >= OFFERS_LIMIT ? this.onOpenOverLimit(OFFER) : this.onOpenOfferModal()
                    }
                  >
                    <FormattedMessage id="TransactionPanel.counterOffer" />
                  </Button>
                )}
                {stateData.showCancellOffer && (
                  <Button
                    className={classNames(css.actionButton, css.actionButtonRed)}
                    onClick={() => this.onSubmitCancellOffer()}
                  >
                    <FormattedMessage id="TransactionPanel.endNegotiation" />
                  </Button>
                )}
                {stateData.showPay && (
                  <Button
                    className={css.actionButton}
                  >
                    <FormattedMessage id="TransactionPanel.pay" />
                  </Button>
                )}

                {isCustomer && !!contract && !signedContract && (
                  <Button
                    className={css.actionButton}
                    onClick={() => this.onOpenUploadFile(CUSTOMER_DOCUMENTS)}
                  >
                    <FormattedMessage id="TransactionPanel.uploadSignedContract" />
                  </Button>
                )}

                {isProvider && showUploadInvoice && (!invoice || !contract) && (
                  <Button
                    className={css.actionButton}
                    onClick={() => this.onOpenUploadFile(PROVIDER_DOCUMENTS)}
                  >
                    <FormattedMessage id="TransactionPanel.uploadDocuments" />
                  </Button>
                )}

                {stateData.showCompleteOfferRequest && (
                  <Button
                    className={css.actionButton}
                    onClick={() => onCompleteOfferRequest(currentTransaction, TRANSITION_REQUEST_OFFER_COMPLETE)}
                  >
                    <FormattedMessage id="TransactionPanel.workCompleted" />
                  </Button>
                )}
                {stateData.showCompleteOfferApprove && (
                  <Button
                    className={css.actionButton}
                    onClick={() => onCompleteOfferRequest(currentTransaction, TRANSITION_ACCEPT_OFFER_COMPLETE)}
                  >
                    <FormattedMessage id="TransactionPanel.approve" />
                  </Button>
                )}
                {stateData.showCompleteOfferDecline && (
                  <Button
                    className={classNames(css.actionButton, css.actionButtonRed)}
                    onClick={() => completeOfferRequestCounter >= COMPLETE_REQUESTS_LIMIT ? this.onOpenOverLimit(REQUEST) : this.onOpenDeclineOfferComplete()}
                  >
                    <FormattedMessage id="TransactionPanel.decline" />
                  </Button>
                )}
                {stateData.showProviderWaitText && (
                  <p className={css.infoText}>
                    <FormattedMessage id="TransactionPanel.waitForAcceptOrDecline" />
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <Modal
          id="ListingPage.enquiry"
          contentClassName={css.enquiryModalContent}
          isOpen={enquiryModalOpen}
          onClose={() => this.setState({ enquiryModalOpen: false })}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <EnquiryForm
            className={css.enquiryForm}
            submitButtonWrapperClassName={css.enquirySubmitButtonWrapper}
            listingTitle={customDisplayName || listingTitle}
            authorDisplayName={authorDisplayName}
            sendEnquiryError={sendEnquiryError}
            onSubmit={this.onSubmitEnquiry}
            inProgress={sendEnquiryInProgress}
            currentListing={currentListing}
            isRequestForQuote
          />
        </Modal>
        <Modal
          id="TransactionPage.offerDetails"
          contentClassName={css.enquiryModalContent}
          isOpen={offerDetailsOpen}
          onClose={() => this.setState({ offerDetailsOpen: false })}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <OfferDetails
            onSubmit={(values) => this.onSubmitOfferDetails(values)}
            isProvider={isProvider}
          />
        </Modal>
        <Modal
          id="TransactionPage.uploadFile"
          contentClassName={css.enquiryModalContent}
          isOpen={uploadFileOpen}
          onClose={() => this.onCloseUploadFile()}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <FileUploadForm
            onSubmit={(values) => this.onSubmitUploadedFile(values)}
            fieldType={uploadFileType}
          />
        </Modal>
        <Modal
          id="TransactionPage.overLimit"
          contentClassName={css.enquiryModalContent}
          isOpen={overLimitOpen}
          onClose={() => this.onCloseOverLimit()}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          {overLimitType === OFFER ? (
            <div>
              <p className={css.attentionTitle}>
                <FormattedMessage id="TransactionPanel.lastCounterOffer" />
              </p>
              <OfferDetails
                onSubmit={(values) => this.onSubmitOfferDetails(values)}
                isProvider={isProvider}
              />
            </div>
          ) : (
            <div>
              <p className={css.attentionTitle}>
                <FormattedMessage id="TransactionPanel.lastCompleteRequest" />
              </p>
              {stateData.showCompleteOfferRequest && (
                <Button
                  className={css.actionButton}
                  onClick={() => {
                    onCompleteOfferRequest(currentTransaction, TRANSITION_REQUEST_OFFER_COMPLETE);
                    this.setState({ overLimitOpen: false });
                  }}
                >
                  <FormattedMessage id="TransactionPanel.workCompleted" />
                </Button>
              )}

              {stateData.showCompleteOfferApprove && (
                <Button
                  className={css.actionButton}
                  onClick={() => {
                    onCompleteOfferRequest(currentTransaction, TRANSITION_ACCEPT_OFFER_COMPLETE);
                    this.setState({ overLimitOpen: false });
                  }}
                >
                  <FormattedMessage id="TransactionPanel.approve" />
                </Button>
              )}
              {stateData.showCompleteOfferDecline && (
                <Button
                  className={classNames(css.actionButton, css.actionButtonRed)}
                  onClick={() => {
                    this.onOpenDeclineOfferComplete()
                    // onCompleteOfferRequest(currentTransaction, TRANSITION_DECLINE_OFFER_COMPLETE);
                    this.setState({ overLimitOpen: false });
                  }}
                >
                  <FormattedMessage id="TransactionPanel.decline" />
                </Button>
              )}
            </div>
          )}
        </Modal>

        <Modal
          id="TransactionPage.declineOfferComplete"
          contentClassName={css.enquiryModalContent}
          isOpen={declineOfferCompleteOpen}
          onClose={() => this.setState({ declineOfferCompleteOpen: false })}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <EndNegotiation
            onSubmit={(values) => this.onDeclineOffer(values)}
            isProvider={isProvider}
          />
        </Modal>

      </div>
    );
  }
}

TransactionPanelComponent.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  acceptSaleError: null,
  declineSaleError: null,
  fetchMessagesError: null,
  initialMessageFailed: false,
  savePaymentMethodFailed: false,
  sendMessageError: null,
  sendReviewError: null,
  monthlyTimeSlots: null,
  nextTransitions: null,
  lineItems: null,
  fetchLineItemsError: null,
};

TransactionPanelComponent.propTypes = {
  rootClassName: string,
  className: string,

  currentUser: propTypes.currentUser,
  transaction: propTypes.transaction.isRequired,
  totalMessagePages: number.isRequired,
  oldestMessagePageFetched: number.isRequired,
  messages: arrayOf(propTypes.message).isRequired,
  initialMessageFailed: bool,
  savePaymentMethodFailed: bool,
  fetchMessagesInProgress: bool.isRequired,
  fetchMessagesError: propTypes.error,
  sendMessageInProgress: bool.isRequired,
  sendMessageError: propTypes.error,
  sendReviewInProgress: bool.isRequired,
  sendReviewError: propTypes.error,
  onFetchTimeSlots: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onShowMoreMessages: func.isRequired,
  onSendMessage: func.isRequired,
  onSendReview: func.isRequired,
  onSubmitBookingRequest: func.isRequired,
  monthlyTimeSlots: object,
  nextTransitions: array,
  // line items
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape,
};

const TransactionPanel = injectIntl(TransactionPanelComponent);

export default TransactionPanel;
